import { faRobot } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherFitnessTestSection from "../../../components/OtherFitnessTestSection";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateStrengthMutation,
  useGetStrengthChartQuery,
  useGetStrengthQuery,
  useUpdateStrengthMutation,
} from "../../../redux/Apis/FitnessTest/Strength";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion, setMetaTags } from "../../../utils/helper";

const StrengthTest = () => {
  const { t } = useTranslate("strengthTest");
  const lang = useSelector((state) => state.language.lang);
  const { FITNESSTEST, CALCULATOR_DATA } = useStaticData();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const [createStrength] = useCreateStrengthMutation();
  const [updateStrength] = useUpdateStrengthMutation();
  const [ageGroup, setAgeGroup] = useState("20-29");
  const [gender, setGender] = useState("male");
  const [exercise, setExercise] = useState("Push-ups");
  const [inputValue, setInputValue] = useState("");
  const [resultText, setResultText] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [rating, setRating] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const {
    data: strengthHistoryData,
    refetch: refetchStrength,
    isLoading,
  } = useGetStrengthQuery({ lang }, { skip: !user });
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const [selectedStrength, setSelectedStrength] = useState(
    CALCULATOR_DATA.STRENGTH[0].value,
  );

  const {
    data: strengthData,
    refetch: refetchStrenghtData,
    isLoading: loadingStrenght,
  } = useGetStrengthChartQuery(
    { type: selectedStrength, startdate: startDate, enddate: endDate, lang },
    { skip: !startDate || !endDate || !user },
  );
  const courseState = location?.state || {};
  const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchStrength();
    }
  }, [user, refetchStrength]);

  useEffect(() => {
    if (startDate && endDate && selectedStrength && user) {
      refetchStrenghtData();
    }
  }, [startDate, endDate, selectedStrength, user, refetchStrenghtData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Strength Test",
      "Test your muscular endurance with push-ups, sit-ups, and wall-sit strength tests.",
    );
  }, []);

  useEffect(() => {
    if (strengthHistoryData) {
      setHistoryData(strengthHistoryData.data || []);
    }
  }, [strengthHistoryData]);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "Not yet calculated";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result}</p>
      </div>
    );
  };

  const calculateRating = (countOrTime) => {
    const val = parseInt(countOrTime);
    if (!val || val < 0) return null;

    const decade = parseInt(ageGroup.split("-")[0]);
    const ageFactor = 1 - ((decade - 20) / 10) * 0.1;

    let rating = t("ratingLevels.poor");

    const getThresholds = () => {
      const standards = {
        "Push-ups": {
          male: [54, 45, 35, 20],
          female: [48, 34, 17, 6],
        },
        "Sit-ups": {
          male: [50, 40, 30, 20],
          female: [45, 35, 25, 15],
        },
        Flexiones: {
          male: [54, 45, 35, 20],
          female: [48, 34, 17, 6],
        },
        Abdominales: {
          male: [50, 40, 30, 20],
          female: [45, 35, 25, 15],
        },
      };
      const base = standards[exercise][gender];
      return base.map((v) => Math.round(v * ageFactor));
    };

    if (exercise === "Wall-Sit") {
      const baseMale = [90, 60, 45, 30];
      const baseFemale = [75, 50, 35, 20];
      const adjust = [0, -5, -10, -15, -20][(decade - 20) / 10];
      const base = gender === "male" ? baseMale : baseFemale;
      const thresholds = base.map((v) => v + adjust);
      const [ex, go, av, ba] = thresholds;
      if (val >= ex) rating = t("ratingLevels.excellent");
      else if (val >= go) rating = t("ratingLevels.good");
      else if (val >= av) rating = t("ratingLevels.average");
      else if (val >= ba) rating = t("ratingLevels.belowaverage");
      return rating;
    }

    const [ex, go, av, ba] = getThresholds();
    if (val >= ex) rating = t("ratingLevels.excellent");
    else if (val >= go) rating = t("ratingLevels.good");
    else if (val >= av) rating = t("ratingLevels.average");
    else if (val >= ba) rating = t("ratingLevels.belowaverage");
    return rating;
  };

  const handleCalculate = () => {
    if (!inputValue || parseInt(inputValue) <= 0) {
      toast.warn(t("messages.enterValidNumber"));
      return;
    }

    const value = parseInt(inputValue);
    const computedRating = calculateRating(value);
    if (!computedRating) {
      toast.error(t("messages.alculationError"));
      return;
    }

    setRating(computedRating);

    const message =
      exercise === "Wall-Sit"
        ? `${t(
            "toastMessages.wallSit",
          )} ${value} seconds – ${computedRating} ${t("toastMessages.default")}`
        : `${t(
            "toastMessages.youDid",
          )} ${value} ${exercise.toLowerCase()} – ${computedRating} ${t(
            "toastMessages.default",
          )}`;

    toast.success(t("messages.calculatedSuccess"));
    setResultText(message);
  };

  const generateAICoach = async (inputResult) => {
    try {
      if (!courseId || !sectionId) return;

      setAiLoading(true);

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "Strength Test",
        result: [
          {
            ageGroup: inputResult.ageGroup,
            gender: inputResult.gender,
            exercise: inputResult.type,
            input: inputResult.input,
            result: inputResult.result,
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    if (!rating) {
      toast.warn(t("messages.calculateBeforeSave"));
      return;
    }

    if (!user) {
      toast.warn(t("messages.loginToSave"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        type: exercise,
        ageGroup,
        gender,
        input: inputValue,
        result: rating,
      };
      if (courseId) payload.course = courseId;
      const response = await createStrength({
        payload,
        params: { lang },
      }).unwrap();
      toast.success(response.message || t("messages.saved"));
      refetchStrength();
      refetchStrenghtData();
      if (courseId && sectionId && templateId) {
        await generateAICoach(payload);
      }
    } catch (err) {
      toast.error(err?.data?.message || t("messages.saveFailed"));
    }
  };

  const handleDelete = async () => {
    try {
      const latest = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latest) {
        await updateStrength({
          id: latest._id,
          isactive: false,
          params: { lang },
        }).unwrap();
        toast.success(t("messages.deletedLastEntry"));
        refetchStrength();
        refetchStrenghtData();
      }
    } catch {
      toast.error(t("messages.deletionFailed"));
    }
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  if (isLoading || loadingStrenght) {
    <Loader loading={true} />;
  }

  const handleStrengthTabChangeFactory = (setSelectedStrength) => {
    return (index) => {
      const selected = CALCULATOR_DATA.STRENGTH[index].value;
      setSelectedStrength(selected);
    };
  };

  const handleStrengthTabChange =
    handleStrengthTabChangeFactory(setSelectedStrength);

  return (
    <>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>
          <div className="row d-flex">
            <div
              className="col-md-12 mt-4 p-5"
              style={{ backgroundColor: "#161617", borderRadius: "2rem" }}
            >
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">
                    {t("testYourStrength")}
                  </h4>
                  <div className="row">
                    <div className="col-md-6">
                      <Input
                        label={t("labels.age")}
                        type="select"
                        options={CALCULATOR_DATA.AGE_GROUP}
                        value={ageGroup}
                        onChange={(e) => setAgeGroup(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t("labels.gender")}
                        type="select"
                        options={CALCULATOR_DATA.GENDER}
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <Input
                        label={t("labels.test")}
                        type="select"
                        options={CALCULATOR_DATA.TEST}
                        value={exercise}
                        onChange={(e) => setExercise(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={exercise}
                        type="number"
                        placeholder={exercise}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="row mt-5">
                    <div
                      className="col-12 bg-white py-2 px-4 d-flex justify-content-between align-items-center"
                      style={{ borderRadius: "0.5rem" }}
                    >
                      <p className="grey-text mb-0">
                        {t("labels.calculateInfo")}
                      </p>
                      <Button
                        text={t("buttons.calculate")}
                        onClick={handleCalculate}
                      />
                    </div>
                  </div>
                  {rating && location.state?.returnTo && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("buttons.continueLearning")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-STRENGTH`,
                              resultData: {
                                type: "STRENGTH",
                                result: rating,
                                value: resultText,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    <h6>
                      {resultText
                        ? `${resultText} ${
                            exercise === "1RM" ? "kg (1RM)" : "reps"
                          }`
                        : t("results.notCalculated")}
                    </h6>
                    <p className="p-sm">{t("results.info")}</p>
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 grey-text p-sm">
                        {t("results.trackInfo")}
                      </p>
                      <Button text={t("buttons.startTraining")} to="/classes" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <FitnessToolsResultCard
              type="Strength"
              result={
                rating
                  ? { result: rating, timestamp: new Date().toISOString() }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSave}
              handleDeleteLast={handleDelete}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={strengthData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
              selectedStrength={selectedStrength}
              handleStrengthTabChange={handleStrengthTabChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "}
                  {t("aiSuggestion.title")}
                </h4>

                {aiLoading ? (
                  <p style={{ color: "#aaa" }}>
                    <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  </p>
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherFitnessTestSection
        data={FITNESSTEST.filter((i) => i.route !== window.location.pathname)}
      />
      <Footer />
    </>
  );
};

export default StrengthTest;
